import { useQuery, useQueryClient } from '@tanstack/react-query';
import { db } from '../lib/supabase.js';

// ─── useSetting ───────────────────────────────────────────────────────────────
// Cached read of one key from the `settings` table. Every consumer of the same
// key shares a single cache entry, so a value saved through useSaveSetting()
// propagates immediately to all mounted views — no per-component copies, no
// divergent defaults, no duplicate fetches on every mount.
export function useSetting(key) {
  return useQuery({
    queryKey: ['setting', key],
    queryFn: () => db.getSetting(key),
    staleTime: 5 * 60 * 1000,
    gcTime:    10 * 60 * 1000,
  });
}

// Boolean convenience for the stock-tracking flag (stored as 'true'/'false').
// Resolves false while loading and on fetch error — every surface fails CLOSED
// (stock states hidden), matching the feature's default-off behavior.
export function useStockTracking() {
  const { data } = useSetting('stock_tracking_enabled');
  return data === 'true';
}

// Whether own (first-party) products sort ahead of vendor products in the
// catalogue. Defaults ON: an unset key (''), a still-loading read (undefined),
// and an explicit 'true' all resolve true; only a stored 'false' turns it off.
export function useOwnProductsFirst() {
  const { data } = useSetting('catalogue_own_first');
  return data !== 'false';
}

// Write-through save: persists to the DB first, then updates the query cache
// so already-mounted consumers pick the change up without a reload. Throws on
// failure BEFORE touching the cache, so the UI never shows an unsaved state.
export function useSaveSetting() {
  const queryClient = useQueryClient();
  return async (key, value) => {
    await db.setSetting(key, value);
    queryClient.setQueryData(['setting', key], value);
  };
}
